import React, { useState, useEffect } from 'react';
import { Receipt, Upload, Check, Loader, Trophy, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const BRAND = 'linear-gradient(135deg,#2cbdc4,#094f6f)';

const STATUS_STYLES = {
  pending:  { cls: 'bg-amber-50 text-amber-700 border border-amber-200',   label: 'Pending Review' },
  approved: { cls: 'bg-green-50 text-green-700 border border-green-200',   label: 'Approved' },
  rejected: { cls: 'bg-red-50   text-red-700   border border-red-200',     label: 'Rejected' },
};

const fmtDate = iso => iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

export default function RentReceiptsTab({ token }) {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [form, setForm]         = useState({
    landlord_name: '', property_address: '', rent_amount: '',
    payment_frequency: 'weekly', receipt_date: '', receipt_url: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg]   = useState('');
  const [error, setError]           = useState('');

  useEffect(() => {
    let active = true;
    async function loadReceipts() {
      setLoading(true);
      try {
        const r = await fetch(`${API}/api/receipts/my`, { headers: { Authorization: `Bearer ${token}` } });
        const d = await r.json();
        if (active) setReceipts(Array.isArray(d) ? d : []);
      } catch { if (active) setReceipts([]); }
      if (active) setLoading(false);
    }
    loadReceipts();
    return () => { active = false; };
  }, [token, refreshKey]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSubmitMsg('');
    if (!form.landlord_name || !form.property_address || !form.rent_amount || !form.receipt_date) {
      setError('Please fill in all required fields.'); return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/receipts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, rent_amount: parseFloat(form.rent_amount) }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.detail || 'Submission failed'); }
      else {
        setSubmitMsg(`Receipt submitted! Our team will verify within 1 business day. Once approved, you'll earn ${Math.round(parseFloat(form.rent_amount))} points.`);
        setForm({ landlord_name: '', property_address: '', rent_amount: '', payment_frequency: 'weekly', receipt_date: '', receipt_url: '' });
        setRefreshKey(k => k + 1);
      }
    } catch { setError('Network error. Please try again.'); }
    setSubmitting(false);
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="space-y-6" data-testid="rent-receipts-tab">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1 flex items-center gap-2">
          <Receipt className="w-5 h-5 text-[#094f6f]" /> My Rent
        </h2>
        <p className="text-sm text-gray-500">Submit a rent receipt to earn points equal to your rent amount. One verified receipt per calendar week.</p>
      </div>

      {/* Info banner */}
      <div className="flex gap-3 bg-sky-50 border border-sky-200 rounded-2xl p-4">
        <Trophy className="w-5 h-5 text-[#094f6f] flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-[#094f6f]">Earn points = your rent amount</p>
          <p className="text-xs text-gray-500 mt-0.5">A $400 rent payment earns 400 points · Verified by our team · 1 receipt per calendar week</p>
        </div>
      </div>

      {/* Submit form */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <h3 className="font-bold text-gray-900 mb-4 text-sm uppercase tracking-wide">Submit a Receipt</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Landlord / Agency Name *</label>
              <input value={form.landlord_name} onChange={e => set('landlord_name', e.target.value)}
                placeholder="e.g. Smith Property Group"
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Property Address *</label>
              <input value={form.property_address} onChange={e => set('property_address', e.target.value)}
                placeholder="e.g. 42 Collins St, Melbourne VIC"
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Rent Amount (AUD) *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">A$</span>
                <input type="number" min="1" step="0.01" value={form.rent_amount} onChange={e => set('rent_amount', e.target.value)}
                  placeholder="400.00"
                  className="w-full border border-gray-200 rounded-xl pl-8 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Payment Frequency *</label>
              <select value={form.payment_frequency} onChange={e => set('payment_frequency', e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30 bg-white">
                <option value="weekly">Weekly</option>
                <option value="fortnightly">Fortnightly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Receipt Date *</label>
              <input type="date" value={form.receipt_date} onChange={e => set('receipt_date', e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Receipt Image URL (optional)</label>
            <input value={form.receipt_url} onChange={e => set('receipt_url', e.target.value)}
              placeholder="Paste Cloudinary or image URL of your receipt"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30" />
            <p className="text-xs text-gray-400 mt-1">Providing a receipt image speeds up verification</p>
          </div>

          {form.rent_amount && !isNaN(parseFloat(form.rent_amount)) && (
            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
              <Trophy className="w-4 h-4 text-emerald-600" />
              <p className="text-sm text-emerald-700 font-medium">
                This receipt will earn <strong>{Math.round(parseFloat(form.rent_amount))} points</strong> once approved
              </p>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
          {submitMsg && (
            <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
              <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-700">{submitMsg}</p>
            </div>
          )}

          <button type="submit" disabled={submitting}
            data-testid="submit-receipt-btn"
            className="flex items-center gap-2 px-6 py-3 rounded-xl text-white text-sm font-bold disabled:opacity-60 hover:opacity-90 transition-all"
            style={{ background: BRAND }}>
            {submitting ? <Loader className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {submitting ? 'Submitting…' : 'Submit Receipt'}
          </button>
        </form>
      </div>

      {/* Receipt history */}
      <div>
        <h3 className="font-bold text-gray-900 mb-3 text-sm uppercase tracking-wide">Receipt History</h3>
        {loading ? (
          <div className="py-8 text-center text-gray-400 text-sm flex items-center justify-center gap-2">
            <Loader className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : receipts.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-sm">
            No receipts submitted yet. Submit your first rent receipt above to start earning points.
          </div>
        ) : (
          <div className="space-y-3">
            {receipts.map((r, i) => {
              const s = STATUS_STYLES[r.status] || STATUS_STYLES.pending;
              return (
                <div key={i} className="bg-white border border-gray-100 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">{r.property_address}</p>
                    <p className="text-xs text-gray-500">{r.landlord_name} · {r.payment_frequency} · {fmtDate(r.created_at)}</p>
                    {r.admin_note && <p className="text-xs text-gray-400 mt-1 italic">&ldquo;{r.admin_note}&rdquo;</p>}
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <p className="font-black text-lg text-[#094f6f]">A${r.rent_amount?.toFixed(2)}</p>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${s.cls}`}>{s.label}</span>
                    {r.status === 'approved' && r.points_awarded > 0 && (
                      <span className="text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full flex items-center gap-1">
                        <Trophy className="w-3 h-3" /> +{r.points_awarded} pts
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
